import React, { useEffect, useState } from "react";
import { ShieldCheck, LogOut, Radio, BellRing, Volume2, VolumeX, Activity } from "lucide-react";
import { useCountryConfig } from "../lib/useCountryConfig";

/**
 * CommandHeader — mission-control style header for the admin console.
 *
 * Stacks three bands:
 *   1. CLASSIFICATION banner (diagonal yellow/black NATO-restricted stripe)
 *   2. Identification row: SOS-MALI shield + ops code + dual clock (UTC | Bamako)
 *      + RESTREINT pill + dispatcher logout
 *   3. Title row: "ADMINISTRATION" (display) + subtitle
 *
 * No props needed beyond onLogout — the live clock state is self-contained.
 */
export const CommandHeader = ({
  onLogout,
  unackedCount = 0,
  firehoseLive = false,
  audioEnabled = true,
  audioUnlocked = true,
  onOpenAudioSettings,
  onUnlockAudio,
  systemHealth = null,
  onOpenSystemHealth,
}) => {
  const cfg = useCountryConfig();
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const utc = now.toUTCString().slice(17, 25); // HH:MM:SS
  // Country-local clock label — falls back to "LOC" if the country config
  // omits a timezone_label (which only sosmali.com legacy data would).
  const localTzLabel = (cfg.timezone_label || "LOC").slice(0, 3).toUpperCase();
  const local = now.toUTCString().slice(17, 25);
  const dateStr = now.toLocaleDateString("fr-FR", { weekday: "short", day: "2-digit", month: "short", year: "numeric" });
  const jday = Math.floor((Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()) - Date.UTC(now.getUTCFullYear(), 0, 0)) / 86400000);

  // Traffic-light health pill — count degraded + down dependencies so the
  // dispatcher sees a pane before the citizen calls in.
  const overall = systemHealth?.overall || "unknown";
  const deps = systemHealth?.dependencies || {};
  const issueCount = Object.values(deps).filter(
    (d) => d?.status === "down" || d?.status === "degraded"
  ).length;
  const healthClasses =
    overall === "down"
      ? "border-rose-500/60 bg-rose-500/15 text-rose-200 cmd-unacked-pulse"
      : overall === "degraded"
      ? "border-amber-500/60 bg-amber-500/15 text-amber-200"
      : overall === "ok"
      ? "border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
      : "border-zinc-700 text-zinc-400 hover:text-zinc-300";
  const healthLabel =
    overall === "down"
      ? "Panne"
      : overall === "degraded"
      ? "Dégradé"
      : overall === "ok"
      ? "Nominal"
      : "Sonde…";

  return (
    <header className="border-b border-zinc-800" data-testid="cmd-header">
      {/* 1 · Mali tricolour accent — subtle 2px line, not a hazard stripe.
            Calmer, more official; communicates national identity
            without shouting. */}
      <div className="h-[2px] flex" aria-hidden="true" data-testid="cmd-mali-accent">
        <span className="flex-1" style={{ background: "#14B53A" }} />
        <span className="flex-1" style={{ background: "#FCD116" }} />
        <span className="flex-1" style={{ background: "#CE1126" }} />
      </div>
      {/* Classification banner — calmer dark navy, no diagonal stripe. */}
      <div className="bg-[#0b0f14] border-b border-zinc-800/80 px-3 sm:px-5 py-1 flex items-center justify-between text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-400">
        <span data-testid="cmd-classification" className="inline-flex items-center gap-1.5">
          <ShieldCheck className="w-3 h-3 text-emerald-400" /> Restreint · usage officiel uniquement
        </span>
        <span className="hidden sm:inline text-zinc-600 font-mono">SOS-ML · DPSC · OPS</span>
      </div>

      {/* 2 · Identification row */}
      <div className="bg-[#0b0f14] px-3 sm:px-5 py-2.5 flex items-center justify-between gap-3 border-b border-zinc-800/80">
        <div className="flex items-center gap-2 min-w-0">
          <ShieldCheck className="w-4 h-4 text-emerald-400 shrink-0" strokeWidth={2.5} />
          <span className="font-mono text-[11px] sm:text-xs font-semibold tracking-[0.12em] text-emerald-300 truncate" data-testid="cmd-opscode">
            SOS-ML-OPS-001
          </span>
          <span className="hidden sm:inline-block text-zinc-700">·</span>
          <span className="hidden sm:inline-block font-mono text-[10px] text-zinc-500" data-testid="cmd-jday">
            J{String(jday).padStart(3, "0")}
          </span>
        </div>

        <div className="flex items-center gap-3">
          {/* System health traffic-light — clicks open SystemHealthDrawer */}
          <button
            type="button"
            onClick={onOpenSystemHealth}
            data-testid="cmd-health-badge"
            title={
              overall === "down"
                ? `${issueCount} dépendance(s) en panne — cliquer pour le détail`
                : overall === "degraded"
                ? `${issueCount} dépendance(s) dégradée(s) — cliquer pour le détail`
                : overall === "ok"
                ? "Tous systèmes opérationnels"
                : "Sonde en cours…"
            }
            className={`relative shrink-0 inline-flex items-center gap-1.5 px-2 py-1 border text-[10px] font-bold uppercase tracking-[0.18em] transition-colors ${healthClasses}`}
          >
            <Activity className="w-3.5 h-3.5" />
            <span data-testid="cmd-health-label">{healthLabel}</span>
            {issueCount > 0 && (
              <span
                data-testid="cmd-health-issue-count"
                className="tabular-nums ml-0.5"
              >
                {issueCount}
              </span>
            )}
          </button>

          {/* Firehose live indicator + unacked counter */}
          <button
            type="button"
            onClick={onOpenAudioSettings}
            data-testid="cmd-unacked-badge"
            title={unackedCount > 0 ? `${unackedCount} alerte(s) non acquittée(s) — Réglages son` : "Réglages son"}
            className={`relative shrink-0 inline-flex items-center gap-1.5 px-2 py-1 border text-[10px] font-bold uppercase tracking-[0.18em] transition-colors ${
              unackedCount > 0
                ? "border-rose-500/60 bg-rose-500/15 text-rose-200 cmd-unacked-pulse"
                : "border-zinc-700 text-zinc-400 hover:border-emerald-500/50 hover:text-emerald-300"
            }`}
          >
            <BellRing className="w-3.5 h-3.5" />
            <span className="tabular-nums" data-testid="cmd-unacked-count">{unackedCount}</span>
            <span className={`absolute -top-1 -right-1 w-1.5 h-1.5 rounded-full ${
              firehoseLive ? "bg-emerald-400 cmd-pulse-dot" : "bg-zinc-600"
            }`} title={firehoseLive ? "Firehose connecté" : "Firehose hors-ligne"} />
          </button>

          {/* Audio mute / unlock */}
          {!audioUnlocked ? (
            <button
              type="button"
              onClick={onUnlockAudio}
              data-testid="cmd-audio-unlock"
              title="Activer les sons d'alerte (requis par le navigateur après chargement de la page)"
              className="shrink-0 inline-flex items-center gap-1 px-2 py-1 border border-amber-500/60 bg-amber-500/10 text-amber-200 text-[10px] font-bold uppercase tracking-[0.18em] hover:bg-amber-500/20"
            >
              <VolumeX className="w-3.5 h-3.5" /> Activer
            </button>
          ) : (
            <button
              type="button"
              onClick={onOpenAudioSettings}
              data-testid="cmd-audio-settings"
              title="Réglages son"
              className={`shrink-0 inline-flex items-center gap-1 px-2 py-1 border text-[10px] font-bold uppercase tracking-[0.18em] transition-colors ${
                audioEnabled
                  ? "border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/15"
                  : "border-zinc-700 text-zinc-500 hover:text-zinc-300"
              }`}
            >
              {audioEnabled ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5" />} Son
            </button>
          )}

          <div className="text-right leading-tight">
            <div className="font-mono text-[10px] sm:text-xs text-zinc-100 tabular-nums tracking-wider" data-testid="cmd-clock-utc">
              <span className="text-zinc-500 mr-1">UTC</span>{utc}
            </div>
            <div className="font-mono text-[9px] text-zinc-500 tabular-nums tracking-wider hidden sm:block" data-testid="cmd-clock-local">
              {localTzLabel} {local}
            </div>
          </div>
          <span className="cmd-pulse-dot w-2 h-2 rounded-full bg-emerald-400 shrink-0" aria-hidden="true" />
          <button
            type="button"
            onClick={onLogout}
            data-testid="admin-logout-btn"
            className="shrink-0 border border-zinc-700 text-zinc-200 px-2.5 py-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] hover:border-emerald-500/60 hover:text-emerald-300 transition-colors flex items-center gap-1.5"
          >
            <LogOut className="w-3 h-3" /> Sortir
          </button>
        </div>
      </div>

      {/* 3 · Title row */}
      <div className="bg-[#0b0f14] px-3 sm:px-5 py-3 flex items-end justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[10px] font-semibold tracking-[0.18em] uppercase text-zinc-500 flex items-center gap-1.5">
            <Radio className="w-3 h-3 text-emerald-400" /> Centre de modération · {cfg.name || "Mali"}
          </div>
          <h1
            className="font-display text-2xl sm:text-3xl font-bold tracking-tight leading-none mt-1 text-zinc-100"
            data-testid="cmd-title"
          >
            Administration
          </h1>
        </div>
        <div className="hidden sm:block text-right font-mono text-[10px] text-zinc-500 tabular-nums tracking-wider">
          {dateStr}
        </div>
      </div>
    </header>
  );
};

export default CommandHeader;
